import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, User, ExternalLink } from 'lucide-react';
import { useAgents } from '../context/AgentsContext';
import { createAppUser, deleteAppUser } from '../lib/api';
import type { Agent } from '../types';

const AdminAgents = () => {
  const navigate = useNavigate();
  const { agents, loading, error, refresh, canManageAgents } = useAgents();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    displayName: '',
    nickname: '',
    profileEmail: '',
    username: '',
    password: '',
  });

  const resetForm = () => {
    setForm({
      displayName: '',
      nickname: '',
      profileEmail: '',
      username: '',
      password: '',
    });
    setFormError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageAgents) return;
    setFormError(null);
    const displayName = form.displayName.trim();
    const username = form.username.trim();
    const password = form.password;
    if (!displayName || !username || password.length < 8) {
      setFormError('Full name, username, and password (8+ characters) are required.');
      return;
    }
    setSaving(true);
    try {
      await createAppUser({
        displayName,
        username,
        password,
        nickname: form.nickname.trim() || undefined,
        profileEmail: form.profileEmail.trim() || undefined,
      });
      await refresh();
      resetForm();
      setShowForm(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent: Agent) => {
    if (!canManageAgents) return;
    if (!window.confirm(`Remove agent "${agent.name}"? They will no longer be able to sign in.`)) return;
    setSaving(true);
    try {
      await deleteAppUser(agent.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not delete agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.025em' }}>Travel agents</h2>
          <p style={{ color: 'var(--text-muted)', maxWidth: 520, lineHeight: 1.5 }}>
            Add real agent names for quotes, checkout, and emails. Each agent can sign in with their username and password.
          </p>
        </div>
        {canManageAgents && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm((v) => !v);
            }}
            style={{
              background: 'var(--accent)',
              color: 'white',
              padding: '0.65rem 1.25rem',
              borderRadius: '0.75rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Plus size={18} /> Add agent
          </button>
        )}
      </div>

      {!canManageAgents && (
        <p style={{ marginBottom: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '0.75rem', color: '#1e40af', fontSize: '0.9rem' }}>
          Sign in with the main admin account to add or remove agents. You can still open your own workspace below.
        </p>
      )}

      {error && (
        <p style={{ color: '#b91c1c', marginBottom: '1rem', fontWeight: 600 }}>{error}</p>
      )}

      {showForm && canManageAgents && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            display: 'grid',
            gap: '1rem',
            maxWidth: 520,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>New agent</h3>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Full name</span>
            <input
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="Everton P"
              required
            />
          </label>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Nickname (shown in admin)</span>
            <input
              value={form.nickname}
              onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
              placeholder="Everton"
            />
          </label>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Email (quotes &amp; checkout)</span>
            <input
              type="email"
              value={form.profileEmail}
              onChange={(e) => setForm((f) => ({ ...f, profileEmail: e.target.value }))}
              placeholder="agent@yourdomain.com"
            />
          </label>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Login username</span>
            <input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              placeholder="everton"
              required
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Min. 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </label>
          {formError && <p style={{ color: '#b91c1c', margin: 0, fontSize: '0.85rem' }}>{formError}</p>}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="submit"
              disabled={saving}
              style={{ background: 'var(--primary)', color: 'white', padding: '0.6rem 1.25rem', borderRadius: '0.65rem', fontWeight: 700 }}
            >
              {saving ? 'Saving…' : 'Create agent'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading agents…</p>
      ) : agents.length === 0 ? (
        <div
          style={{
            background: 'white',
            border: '1px dashed var(--border)',
            borderRadius: '1rem',
            padding: '2.5rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}
        >
          <User size={40} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
          <p style={{ margin: 0 }}>No agents yet. Add your first travel agent to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {agents.map((agent) => (
            <div
              key={agent.id}
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: '1rem',
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'var(--accent)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                  }}
                >
                  {agent.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{agent.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    <strong>{agent.nickname}</strong>
                    {agent.email ? ` · ${agent.email}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/agent/${agent.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.65rem',
                    background: 'rgba(99, 102, 241, 0.12)',
                    color: 'var(--accent)',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                  }}
                >
                  Open workspace <ExternalLink size={14} />
                </button>
                <Link
                  to={`/admin/agent/${agent.id}/settings`}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.65rem',
                    border: '1px solid var(--border)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: 'var(--text-main)',
                    textDecoration: 'none',
                  }}
                >
                  Settings
                </Link>
                {canManageAgents && (
                  <button
                    type="button"
                    onClick={() => handleDelete(agent)}
                    disabled={saving}
                    aria-label={`Delete ${agent.name}`}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '0.65rem',
                      border: '1px solid #fecaca',
                      color: '#b91c1c',
                      background: '#fef2f2',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAgents;
